import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

type SaleItem = {
  id: string;
  soldAt: number;
  buyerName: string;
  buyerEmail: string;
  planName: string;
  value: number;
  billingType: string;
  kind: "avulsa" | "assinatura";
};

type SalesResponse = {
  label: string;
  count: number;
  total: number;
  days: Array<{ date: string; count: number; total: number }>;
  items: SaleItem[];
};

function currentMonth(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" }).slice(0, 7);
}

function money(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatWhen(ms: number): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDay(iso: string): string {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
}

function methodLabel(value: string): string {
  if (value === "PIX") return "Pix";
  if (value === "CREDIT_CARD") return "Cartão";
  return value || "—";
}

const AdminSalesPanel: React.FC = () => {
  const [month, setMonth] = useState(currentMonth);
  const [date, setDate] = useState("");
  const [data, setData] = useState<SalesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const callable = httpsCallable<{ month?: string; date?: string }, SalesResponse>(functions, "listPlatformSales");
        const result = await callable(date ? { date } : { month });
        if (!cancelled) setData(result.data);
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Não foi possível carregar as vendas.";
          setError(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [month, date]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="mb-1 text-lg font-semibold text-gray-800 sm:text-xl">Vendas</h2>
        <p className="text-sm text-gray-500">
          O que entrou na plataforma. Não é o repasse do terapeuta.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <label className="block text-sm text-gray-700">
          Mês
          <input
            type="month"
            value={month}
            onChange={(event) => {
              setMonth(event.target.value);
              setDate("");
            }}
            className="mt-1 block rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm text-gray-700">
          Dia
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="mt-1 block rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando...
        </div>
      ) : error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>
      ) : !data || data.items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
          Nenhuma venda confirmada neste período.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs uppercase text-gray-500">Vendas</p>
              <p className="text-xl font-semibold text-gray-900">{data.count}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs uppercase text-gray-500">Total</p>
              <p className="text-xl font-semibold text-gray-900">{money(data.total)}</p>
            </div>
          </div>

          {!date && data.days.length > 1 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-2 py-2">Dia</th>
                    <th className="px-2 py-2">Vendas</th>
                    <th className="px-2 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((day) => (
                    <tr key={day.date} className="border-t border-gray-100">
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          className="font-medium text-blue-700 hover:underline"
                          onClick={() => setDate(day.date)}
                        >
                          {formatDay(day.date)}
                        </button>
                      </td>
                      <td className="px-2 py-2">{day.count}</td>
                      <td className="px-2 py-2">{money(day.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-2">Quando</th>
                  <th className="px-2 py-2">Quem</th>
                  <th className="px-2 py-2">O quê</th>
                  <th className="px-2 py-2">Forma</th>
                  <th className="px-2 py-2">Valor</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} className="border-t border-gray-100">
                    <td className="px-2 py-3 whitespace-nowrap">{formatWhen(item.soldAt)}</td>
                    <td className="px-2 py-3">
                      <div className="font-medium text-gray-900">{item.buyerName}</div>
                      {item.buyerEmail && <div className="text-xs text-gray-500">{item.buyerEmail}</div>}
                    </td>
                    <td className="px-2 py-3">
                      <div>{item.planName}</div>
                      <div className="text-xs text-gray-500">{item.kind === "assinatura" ? "Assinatura" : "Compra avulsa"}</div>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">{methodLabel(item.billingType)}</td>
                    <td className="px-2 py-3 whitespace-nowrap font-semibold">{money(item.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminSalesPanel;
